import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import { ShoppingCart, Calendar } from 'lucide-react';
import Layout from '../Layout';
import { getData } from '../../helpers/ApiSettings';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { API_BASE_URL } from '../../helpers/ApiSettings';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { registerLocale, setDefaultLocale } from "react-datepicker";
import fr from 'date-fns/locale/fr';
import '../../css/product.css';
import { CartManager } from '../../helpers/cart';
import { showToast } from '../../helpers/customToast';
import { checkProductAvailability, getUnavailableDateRanges } from '../../helpers/availability';
import QuantitySelector from '../QuantitySelector';

registerLocale('fr', fr);
setDefaultLocale('fr');

const ProductDetailPage = () => {
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [totalPrice, setTotalPrice] = useState(0);
    const [unavailableDates, setUnavailableDates] = useState([]);
    const [selectedQuantity, setSelectedQuantity] = useState(1);
    const [availabilityInfo, setAvailabilityInfo] = useState({ 
        available: true, 
        availableQuantity: 0,
        isChecking: false
    });

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const response = await getData(`/products/${id}?populate=*`);
                // console.log(response.data);
                setProduct(response.data.data);
            } catch (error) {
                console.error('Error fetching product:', error);
            }
        };

        fetchProduct();
    }, [id]);

    useEffect(() => {
        if (product && startDate && endDate) {
            const days = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24) + 1);
            // console.log(days)
            let price;
            if (days <= 30) {
                // Find the appropriate price tier
                const priceTier = product.price.find(tier => days <= tier.nb_days);
                price = priceTier ? priceTier.price : product.daily_price * days;
            } else {
                price = days * product.daily_price;
            }

            setTotalPrice(price);
        } else {
            setTotalPrice(0);
        }
    }, [product, startDate, endDate, selectedQuantity]);
    
    useEffect(() => {
        const loadUnavailableDates = async () => {
            if (product) {
                const ranges = await getUnavailableDateRanges(
                    product.id,
                    selectedQuantity
                );
                setUnavailableDates(ranges);
            }
        };
        
        loadUnavailableDates();
    }, [product, selectedQuantity]);

    useEffect(() => {
        const checkAvailability = async () => {
            if (product && startDate && endDate) {
                setAvailabilityInfo(prev => ({ ...prev, isChecking: true }));
                
                const info = await checkProductAvailability(
                    product.id, 
                    startDate, 
                    endDate,
                    selectedQuantity
                );
                
                setAvailabilityInfo({
                    ...info,
                    isChecking: false
                });

                // Automatically adjust selected quantity if it exceeds available quantity
                if (info.availableQuantity < selectedQuantity) {
                    setSelectedQuantity(Math.max(1, info.availableQuantity));
                }
            }
        };

        checkAvailability();
    }, [product, startDate, endDate, selectedQuantity]);

    const excludeDates = unavailableDates.reduce((dates, range) => {
        const current = new Date(range.start);
        while (current <= range.end) {
            dates.push(new Date(current));
            current.setDate(current.getDate() + 1);
        }
        return dates;
    }, []);

    const handleAddToCart = async () => {
        if (!startDate || !endDate) {
            showToast('Veuillez sélectionner les dates de réservation.', 'error');
            return;
        }

        // Recheck availability right before adding to cart
        const currentAvailability = await checkProductAvailability(
          product.id,
          startDate,
          endDate,
          selectedQuantity
        );

        if (!currentAvailability.available || currentAvailability.availableQuantity < selectedQuantity) {
          showToast('Ce produit n\'est plus disponible pour les dates sélectionnées.', 'error');
          // Update the availability info state
          setAvailabilityInfo({
            available: false,
            availableQuantity: currentAvailability.availableQuantity,
            isChecking: false
          });
          return;
        }

        if (availabilityInfo.availableQuantity < selectedQuantity) {
            showToast('La quantité sélectionnée n\'est plus disponible pour ces dates.', 'error');
            return;
        }

        const cartItem = {
          id: product.id,
          price: totalPrice,
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          quantity: selectedQuantity,
          product: product
        };
        // console.log('Added to cart:', cartItem);

        if(CartManager.addToCart(cartItem)){
            showToast('Produit ajouté au panier !', 'success');
        }else{
            showToast('Cet article est déjà dans votre panier.', 'warn');
        }
    };

    if (!product) {
        return <Layout><div className="container mt-5">Loading...</div></Layout>;
    }

    const renderAvailabilityStatus = () => {
        if (!startDate || !endDate) {
            return null;
        }

        if (availabilityInfo.isChecking) {
            return (
                <div className="text-info">
                    Vérification de la disponibilité...
                </div>
            );
        }

        if (availabilityInfo.availableQuantity === 0) {
            return (
                <div className="text-danger">
                    Non disponible pour ces dates
                </div>
            );
        }

        if (availabilityInfo.available) {
            return (
                <div className="text-success">
                    {availabilityInfo.availableQuantity} unité(s) disponible(s) pour ces dates
                </div>
            );
        } else {
            return (
                <div className="text-danger">
                    Quantité insuffisante pour ces dates 
                    (disponible: {availabilityInfo.availableQuantity})
                </div>
            );
        }
    };

    const renderQuantitySelector = () => {
        if (!startDate || !endDate) return null;
    
        const maxQuantity = Math.max(1, Math.min(
            product.quantity || 1, 
            availabilityInfo.availableQuantity
        ));
    
        return (
            <div className="my-4">
                <label className="form-label mb-0 me-2">Quantité :</label>
                <QuantitySelector
                    value={selectedQuantity}
                    onChange={setSelectedQuantity}
                    maxQuantity={maxQuantity}
                    disabled={availabilityInfo.availableQuantity === 0}
                />
            </div>
        );
    };

    return (
        <Layout>
            <div className="container mt-5 mb-5">
                <div className="row">
                    <div className="col-md-6">
                        <Swiper
                            modules={[Navigation, Pagination]}
                            spaceBetween={10}
                            slidesPerView={1}
                            navigation
                            pagination={{ clickable: true }}
                            className="product-swiper"
                        >
                            {product.images.map((image, index) => (
                                <SwiperSlide key={index} className="product-swiper-slide">
                                    <div className="image-container">
                                        <img
                                            src={API_BASE_URL + image.url}
                                            alt={`produit ${index + 1}`}
                                            className="img-fluid rounded product-image-detail"
                                        />
                                    </div>
                                </SwiperSlide>
                            ))}
                        </Swiper>
                    </div>
                    <div className="col-md-6">
                        <h1 className="mb-4">{product.titre}</h1>
                        <p 
                            className="mb-4" 
                            dangerouslySetInnerHTML={{ __html: product.description }}
                        />
                        <div className="mb-4">
                            <h5>Dates de réservation:</h5>
                            <div className="d-flex align-items-center mb-3">
                                <Calendar className="me-2" />
                                <DatePicker
                                    selected={startDate}
                                    onChange={(date) => setStartDate(date)}
                                    selectsStart
                                    startDate={startDate}
                                    endDate={endDate}
                                    minDate={new Date().setDate(new Date().getDate() + 2)}
                                    excludeDates={excludeDates}
                                    className="form-control"
                                    placeholderText="Date de début"
                                    dateFormat="dd/MM/yyyy"
                                    locale="fr"
                                />
                            </div>
                            <div className="d-flex align-items-center mb-3">
                                <Calendar className="me-2" />
                                <DatePicker
                                    selected={endDate}
                                    onChange={(date) => setEndDate(date)}
                                    selectsEnd
                                    startDate={startDate}
                                    endDate={endDate}
                                    minDate={startDate || new Date().setDate(new Date().getDate() + 2)}
                                    excludeDates={excludeDates}
                                    className="form-control"
                                    placeholderText="Date de fin"
                                    dateFormat="dd/MM/yyyy"
                                    locale="fr"
                                />
                            </div>
                        </div>
                        <p>{renderAvailabilityStatus()}</p>
                        <div>{renderQuantitySelector()}</div>
                        {totalPrice > 0 && (
                            <p className="mb-4"><b>Prix total: {totalPrice.toFixed(2)}</b></p>
                        )}
                        <div className="d-flex mb-4">
                        <button 
                            className="btn btn-primary me-2" 
                            onClick={handleAddToCart}
                            disabled={
                                !availabilityInfo.available || 
                                availabilityInfo.isChecking || 
                                !startDate || 
                                !endDate || 
                                availabilityInfo.availableQuantity < selectedQuantity
                            }
                        >
                            <ShoppingCart className="me-2" />
                            Ajouter au panier
                        </button>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default ProductDetailPage;